import { useMsal } from "@azure/msal-react";
import {
    ChoiceGroup,
    DefaultButton,
    FontWeights,
    IPanelProps,
    IRenderFunction,
    Panel,
    PanelType,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField
} from "@fluentui/react";
import { RootState } from "app/store";
import SimpleCard, { ECardIcon } from "components/controls/SimpleCard";
import { createFeesAndPremiums, emitCustomEvent, fadeIn, panelButtonsStyles, sectionStackTokens } from "global/Application";
import { Constants } from "global/Application/Constants";
import { useApplication } from "global/Application/Context";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { addUpdateFeesAndPremiums, updatePlanFeesAndPremiums } from "global/Reducer/dataSlice";
import { JsonTransformer } from "global/Transform/JsonTransformer";
import { updateArrayReplace } from "global/Utility/TSFunctions";
import {
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot
} from "model/CRM";
import { IPanelEvent, IPickItem, IValueChoiceGroupOption } from "model/Extension";
import {
    IFeesAndPremiums as FAM_IFeesAndPremiums,
    IPlan as FAM_IPlan
} from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { ConnectedProps, connect, useDispatch } from "react-redux";
import SnapshotLookup from "./Controls/SnapshotLookup";
import FeesAndPremiumCard from "./FeesAndPremiumCard";
import FeesAndPremiumExportImportWizard from "./FeesAndPremiumExportImportWizard";


interface IValue {
    feesAndPremiums: FAM_IFeesAndPremiums[]
}

interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IValue>) => void;

    // start condition
    defaultValue?: IValue;

    currentPlan?: FAM_IPlan;

    variant: "entry" | "add" | "select"
}

enum EView {
    entry,
    add,
    select,
    copy,
    import,
    massEdit
};

const FeesAndPremiumWizard = (props: IProps) => {
    const dispatch = useDispatch();

    // current view state/stages
    const [view, setView] = useState<EView | null>(EView.entry);
    const [optionView, setOptionSource] = useState<IValueChoiceGroupOption<EView> | null>(null);
    const [isEditOpen, setIsEditOpen] = useState(false);

    const [entityCollection, setEntityCollection] = useState<IPickItem<FAM_IFeesAndPremiums>[] | null>(null);

    const application = useApplication();
    const [loading, setLoading] = useState<Boolean>(false);

    const [importSnapshot, setImportSnapshot] = useState<IXrmTypes_bcbs_groupconfigurationsnapshot | null>();

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const disabledAdd = !application.ChangeTypePermissions.feesAndPremium.add || props.variant !== "add";
    const disabledLink = props.variant === "add" || !application.ChangeTypePermissions.plan.link_feesandpremium;
    const disabledMassEdit = !application.ChangeTypePermissions.feesAndPremium.import; //props.variant !== "add";

    const [importExportVisible, setImportExportVisible] = useState<boolean>(true);

    const optionsView: IValueChoiceGroupOption<EView>[] = [
        { key: 'add', text: 'Add - New Fees And Premium', data: EView.add, disabled: disabledAdd },
        { key: 'pick', text: 'Pick - From Current Configuration', data: EView.select, disabled: disabledLink },
        { key: 'copy', text: 'Copy/Clone - From Current Configuration', data: EView.copy, disabled: disabledAdd },
        { key: 'import', text: 'Copy/Clone - From External Configuration/BlueRelationship', data: EView.import, disabled: disabledAdd },
        { key: 'massEdit', text: 'Mass Edit - Export Fees and Edit In Excel to Import Edits', data: EView.massEdit, disabled: disabledMassEdit },
    ];

    const singleSelect = props.currentPlan ? true : false;

    useEffect(() => {
        if (props.isOpen) {
            setView(EView.entry);
            setEntityCollection(null);
            setImportSnapshot(null);
        }

    }, [props.isOpen]);


    useEffect(() => {
        if (view === EView.entry) {
            setEntityCollection(null);
            setImportSnapshot(null);
            setEntityCollection(null);
        }
        else if (view === EView.copy || view === EView.import) {
            (async () => {
                try {
                    setLoading(true);

                    setEntityCollection(null);

                    const { jClone, sClone } = await (async () => {

                        if (view === EView.import && importSnapshot) {
                            const result = await dataSource.downloadGroupConfigurationSnapshot(importSnapshot);

                            if (!result)
                                throw new Error('Snapshot not found!');

                            return { jClone: result.json, sClone: structuredClone(importSnapshot) };
                        }
                        else if (view === EView.copy)
                            return { jClone: structuredClone(props.state.data.json), sClone: structuredClone(props.state.data.snapshot) };

                        return { jClone: null, sClone: null };
                    })();

                    if (!jClone || !sClone)
                        return;

                    const transformer = JsonTransformer.getTransformerForCopyInternal(
                        props.state.data.config,
                        dataSource,
                        props.state.data.snapshot,
                        { applyToFeesAndPremiums: true }
                    );

                    await transformer.transformAsync(jClone, sClone);

                    const data = jClone;

                    const feesAndPremiums: IPickItem<FAM_IFeesAndPremiums>[] = [];

                    data.feesAndPremiums.forEach(eachFeeAndPremium => {

                        // transformer above is already taking care of this
                        // lets skip deactivated, they shouldn't be able to pick
                        if (eachFeeAndPremium.deactivated)
                            return;

                        feesAndPremiums.push({
                            item: eachFeeAndPremium,
                            selected: false
                        });
                    });

                    setEntityCollection(feesAndPremiums);
                } catch (error) {

                    // lets create a new one is payload is not providing one
                    emitCustomEvent({
                        eventType: 'globalException',
                        details: error
                    });

                } finally {
                    setLoading(false);
                }

            })();
        }
    }, [view, importSnapshot]);

    const onRenderEntry = () => {
        return (
            <ChoiceGroup
                options={optionsView}
                label="Choose Source"
                required={true}
                selectedKey={optionView?.key}
                onChange={(ev, option?: IValueChoiceGroupOption<EView>) => {
                    setOptionSource(option ?? null);
                }}
            />
        );
    }

    const onRenderEntryFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton
                text='Continue'
                styles={panelButtonsStyles}
                disabled={optionView === null}
                onClick={() => { 
                    if (optionView?.data === EView.massEdit) {
                        setIsEditOpen(true);
                    }else{
                        setView(optionView?.data!); 
                    }
                
                }}
            />
            <DefaultButton
                text='Cancel'
                styles={panelButtonsStyles}
                onClick={() => { props.onCancel?.(); }}
            />
        </div>;
    }

    const onRenderAdd = () => {
        if (entityCollection?.length !== 1) {
            const t = createFeesAndPremiums(props.state);
            setEntityCollection([{ item: t, selected: true }]);
            return <div>Fees And Premium unavailable!</div>
        }

        return (
            <div className={fadeIn}>
                <TextField
                    label='Name'
                    value={entityCollection[0].item.name ?? ''}
                    onChange={(event, value) => {
                        setEntityCollection(xCollection => {
                            const nCollection = [...xCollection!];
                            nCollection[0].item.name = value!;
                            return nCollection;
                        });
                    }}
                />
                <Separator />
            </div>
        )
    }

    const onRenderAddFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton text='Add'
                styles={panelButtonsStyles}
                disabled={!entityCollection || entityCollection.length !== 1 || !entityCollection[0].item.name}
                onClick={() => {
                    const item = entityCollection![0].item;
                    dispatch(addUpdateFeesAndPremiums([item]));

                    if (props.currentPlan && item)
                        dispatch(updatePlanFeesAndPremiums({ planRef: props.currentPlan, feesAndPremiumRef: item }))

                    setEntityCollection(null);
                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    const onRenderSelect = () => {
        // TODO this needs to be a side effect rather than part of render cycle
        if (!entityCollection) {
            const collection = props.state.data.json.feesAndPremiums.map(t => {
                return {
                    item: t,
                    selected: false
                } as (IPickItem<FAM_IFeesAndPremiums>)
            });

            setEntityCollection(collection);

            return (
                <div>
                    Not Available
                </div>
            );
        }

        return (
            <div className={fadeIn}>
                <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                    Choose Fees And Premiums
                </Text>
                <Stack
                    horizontal
                    horizontalAlign="start"
                    tokens={sectionStackTokens}
                    wrap
                    verticalFill
                >
                    {entityCollection.map(eachEntity => {

                        if (eachEntity.item.deactivated)
                            return <></>

                        return (
                            <SimpleCard
                                key={eachEntity.item.id}
                                description={eachEntity.item.name ?? ""}
                                selected={eachEntity.selected}
                                icons={ECardIcon.link}
                                deactivated={eachEntity.item.deactivated}
                                onClick={() => {
                                    if (singleSelect) {
                                        const newState = props.state.data.json.feesAndPremiums.map(t => {
                                            return {
                                                item: t,
                                                selected: t.id === eachEntity.item.id
                                            } as (IPickItem<FAM_IFeesAndPremiums>);
                                        });
                                        setEntityCollection(newState);
                                    }
                                    else {
                                        const newState = updateArrayReplace(entityCollection, eachEntity, { ...eachEntity, selected: !eachEntity.selected });
                                        setEntityCollection(newState);
                                    }
                                }}
                            />
                        );
                    })}
                </Stack>
            </div>
        );
    }

    const onRenderSelectFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton
                text='Pick'
                styles={panelButtonsStyles}
                disabled={!entityCollection || !entityCollection.some(q => q.selected)}
                onClick={() => {
                    props.onConfirm?.({
                        value: {
                            feesAndPremiums: entityCollection?.filter(q => q.selected).map(q => q.item) ?? []
                        }
                    });
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    const onRenderCopy = () => {
        return <div className={fadeIn}>
            <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                Choose Fees And Premium
            </Text>
            <Stack
                horizontal
                horizontalAlign="start"
                tokens={sectionStackTokens}
                wrap
                verticalFill
            >
                {
                    loading &&
                    <Spinner label="Please wait..." ariaLive="assertive" labelPosition="right" size={SpinnerSize.large} styles={{ root: { padding: '25px' } }} />
                }

                {entityCollection?.map(eachEntity => {

                    if (eachEntity.item.deactivated)
                        return <></>;

                    return <FeesAndPremiumCard
                        key={eachEntity.item.id}
                        feesAndPremium={eachEntity.item}
                        feesAndPremiumCollection={entityCollection}
                        state={props.state}
                        autoCallout={true}
                        icon={ECardIcon.copy}
                        onClick={() => {
                            const newState = updateArrayReplace(entityCollection, eachEntity, { ...eachEntity, selected: !eachEntity.selected });
                            setEntityCollection(newState);
                        }}
                    />
                })}

            </Stack>
        </div>;
    }

    const onRenderCopyFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton
                text='Pick'
                styles={panelButtonsStyles}
                disabled={!entityCollection?.find(t => t.selected)}
                onClick={() => {

                    const items = entityCollection?.filter(q => q.selected).map(q => q.item) ?? [];
                    items.forEach(e => { e.name = e.name + ' - Copied'; });

                    dispatch(addUpdateFeesAndPremiums(items));

                    setEntityCollection(null);
                    setImportSnapshot(null);

                    props.onDismiss?.();

                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    // const onRenderExportImportEdit = () => {
    //     return  <FeesAndPremiumExportImportWizard
    //             isOpen = {isEditOpen}
    //             onDismiss={() => setIsEditOpen(false)}
    //             onCancel={() => setIsEditOpen(false)}
    //             variant='export'
    //             embedded={false}/>
    // }

    const onRenderImport = () => {

        return <Stack tokens={{ childrenGap: '15' }}>

            <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                Copy Fees
            </Text>

            <SnapshotLookup
                onPick={async (value) => {
                    const snapshot = await dataSource.getGroupConfigurationSnapshot(value?.id!);
                    setImportSnapshot(snapshot);
                }}
                searchOnMount={true}
            />

            {
                loading &&
                <Spinner label="Please wait..." ariaLive="assertive" labelPosition="right" size={SpinnerSize.large} styles={{ root: { padding: '25px' } }} />
            }
            <Stack
                horizontal
                horizontalAlign="start"
                tokens={sectionStackTokens}
                wrap
                verticalFill
            >
                {entityCollection?.map(eachEntity => {

                    if (eachEntity.item.deactivated)
                        return <></>;

                    return <FeesAndPremiumCard
                        key={eachEntity.item.id}
                        feesAndPremium={eachEntity.item}
                        feesAndPremiumCollection={entityCollection}
                        state={props.state}
                        autoCallout={true}
                        icon={ECardIcon.copy}
                        onClick={() => {
                            const newState = updateArrayReplace(entityCollection, eachEntity, { ...eachEntity, selected: !eachEntity.selected });
                            setEntityCollection(newState);
                        }}
                    />;
                })}
            </Stack>
        </Stack>;
    }

    const onRenderImportFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton
                text='Pick'
                styles={panelButtonsStyles}
                disabled={!entityCollection || !entityCollection.some(q => q.selected)}
                onClick={() => {

                    const items = entityCollection?.filter(q => q.selected).map(q => q.item) ?? [];
                    items.forEach(e => { e.name = e.name + ' - Copied'; });

                    dispatch(addUpdateFeesAndPremiums(items));

                    setEntityCollection(null);
                    setImportSnapshot(null);

                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }


    const onRenderBody: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        let onRender = onRenderEntry;
        switch (view) {
            case EView.entry:
                onRender = onRenderEntry;
                break;

            case EView.add:
                onRender = onRenderAdd;
                break;

            case EView.select:
                onRender = onRenderSelect;
                break;

            case EView.copy:
                onRender = onRenderCopy;
                break;

            case EView.import:
                onRender = onRenderImport;
                break;

            // case EView.massEdit:
            //     onRender = onRenderExportImportEdit;
            //     break;
        }

        return (
            <div>
                <Separator />
                {onRender()}
            </div>
        );
    }

    const onRenderFooter: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        let onRender = onRenderEntry;

        switch (view) {
            case EView.entry:
                onRender = onRenderEntryFooter;
                break;

            case EView.add:
                onRender = onRenderAddFooter;
                break;

            case EView.select:
                onRender = onRenderSelectFooter;
                break;

            case EView.copy:
                onRender = onRenderCopyFooter;
                break;

            case EView.import:
                onRender = onRenderImportFooter;
                break;

            // case EView.massEdit:
            //     onRender = onRenderEntryFooter;
            //     break;
        }

        return onRender();
    }

    return (
        <>
            <FeesAndPremiumExportImportWizard
                isOpen = {isEditOpen}
                onDismiss={() => {setIsEditOpen(false); props.onCancel?.() }}
                onCancel={() => { setIsEditOpen(false); props.onCancel?.() }}
                variant='export'
                embedded={false}/>
            <Panel
                isOpen={props.isOpen}
                onDismiss={props.onCancel}
                type={PanelType.medium}
                closeButtonAriaLabel="Close"
                headerText="Fees & Premiums | Manage"
                isFooterAtBottom={true}
                onRenderFooterContent={onRenderFooter}
            >
                {onRenderBody()}
            </Panel>
        </>
    )
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(FeesAndPremiumWizard);